import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../../Constant";
import { useTranslation } from "react-i18next";
import "./NotificationsDropdown.scss";

const NOTIFICATION_ICONS = {
  order: "fa-shopping-bag",
  orders: "fa-shopping-bag",
  message: "fa-comment",
  messages: "fa-comment",
  promotion: "fa-tag",
  system: "fa-cog",
  shipping: "fa-truck",
  review: "fa-star",
  payment: "fa-credit-card",
  rfq: "fa-file-text",
  nda: "fa-shield",
  sponsorship: "fa-heart",
  disputes: "fa-exclamation-triangle",
  price_alerts: "fa-line-chart",
};

const NotificationItem = React.memo(function NotificationItem({ notification, onMarkRead }) {
  const navigate = useNavigate();
  const iconClass = NOTIFICATION_ICONS[notification.notification_type] || "fa-bell";

  const handleClick = () => {
    if (!notification.is_read) {
      onMarkRead(notification.id);
    }
    if (notification.link) {
      navigate(notification.link);
    }
  };

  return (
    <div
      className={`notification-item ${notification.is_read ? "read" : "unread"}`}
      onClick={handleClick}
    >
      <div className={`notification-icon type-${notification.notification_type}`}>
        <i className={`fa ${iconClass}`} aria-hidden="true"></i>
      </div>
      <div className="notification-content">
        <div className="notification-title">
          {notification.title || notification.subject}
        </div>
        <div className="notification-message">
          {notification.message?.length > 80
            ? notification.message.substring(0, 80) + "..."
            : notification.message}
        </div>
        <div className="notification-time">{notification.time_ago}</div>
      </div>
      {!notification.is_read && <div className="notification-dot"></div>}
    </div>
  );
});

export const NotificationsDropdown = ({
  isOpen,
  onClose,
  userToken,
  onUnreadCountUpdate,
}) => {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  const fetchNotifications = useCallback(() => {
    if (!userToken) return;
    setLoading(true);
    fetch(`${API_BASE_URL}/notifications/`, {
      headers: {
        Authorization: `Bearer ${userToken.jwt_token}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status) {
          setNotifications(data.data || []);
          if (onUnreadCountUpdate) {
            onUnreadCountUpdate(data.unread_count || 0);
          }
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Notification fetch error:", err);
        setLoading(false);
      });
  }, [userToken, onUnreadCountUpdate]);

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  const markAsRead = (id) => {
    if (!userToken) return;
    fetch(`${API_BASE_URL}/notifications/${id}/read/`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${userToken.jwt_token}`,
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then(() => {
        setNotifications((prev) =>
          prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
        );
        if (onUnreadCountUpdate) {
          onUnreadCountUpdate((prev) => Math.max(0, prev - 1));
        }
      })
      .catch((err) => {
        console.error("Mark as read error:", err);
      });
  };

  const markAllAsRead = () => {
    if (!userToken) return;
    fetch(`${API_BASE_URL}/notifications/read-all/`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${userToken.jwt_token}`,
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then(() => {
        setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
        if (onUnreadCountUpdate) {
          onUnreadCountUpdate(0);
        }
      })
      .catch((err) => {
        console.error("Mark all as read error:", err);
      });
  };

  const unreadCount = useMemo(() => notifications.filter((n) => !n.is_read).length, [notifications]);

  if (!isOpen) return null;

  return (
    <div className="notifications-dropdown" ref={dropdownRef}>
      <div className="notifications-dropdown-header">
        <span className="notifications-dropdown-title">{t('notifications.title')}</span>
        {unreadCount > 0 && (
          <button className="mark-all-read-btn" onClick={markAllAsRead}>
            {t('notifications.markAllRead')}
          </button>
        )}
      </div>
      <div className="notifications-dropdown-body">
        {loading ? (
          <div className="notifications-empty">{t('notifications.loading')}</div>
        ) : notifications.length === 0 ? (
          <div className="notifications-empty">{t('notifications.noNotifications')}</div>
        ) : (
          notifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onMarkRead={markAsRead}
            />
          ))
        )}
      </div>
      <div className="notifications-dropdown-footer">
        <a href="/account/notifications" className="view-all-link">
          {t('notifications.viewAll')}
        </a>
      </div>
    </div>
  );
};
